import React, { ReactNode } from "react";
import {
  BlueBirdTheme, Button, Divider, Tooltip, Typography,
  useSnackbar,
} from "@kitbird/core";
import styled from "styled-components";
import useCloseReceipt from "../../useCloseReceipt";
import { ContentType } from "../../constants";
import thumbsUpImg from "../../../../../assets/images/thumb_up.png";
import customerServiceImg from "../../../../../assets/images/customer_service.png";
import avatarImg from "../../../../../assets/images/avatar.svg";
import { ReactComponent as CornerArrowIcon } from "../../../../../assets/icons/corner_arrow.svg";
import { ReactComponent as MoneyIcon } from "../../../../../assets/icons/money.svg";
import { ReactComponent as WarningIcon } from "../../../../../assets/icons/warning.svg";
import { ReactComponent as InfoIcon } from "../../../../../assets/icons/info_circled.svg";
import { ReactComponent as BankIcon } from "../../../../../assets/icons/bank.svg";
import { ReactComponent as CopyIcon } from "../../../../../assets/icons/copy.svg";
import { ThemeBackground } from "../../../../../components/Content/Content.style";
import { toRupiah, toStringDate } from "../../../../../utils/common";
import { generateColor, StatusBox, StatusEnums } from "../../../../../components/Status";
import getHasAccessAction from "../../../../../components/Authentication/utils";
import useAuth from "../../../../../components/Authentication/useAuth";
import { CommonTrxTypes } from "../../../../../enums/common";
import { CheckIcon, WaitingIcon } from "../../../../../assets/icons";

interface WhiteReceiptContentProps {
  useCustomHook: ReturnType<typeof useCloseReceipt>;
  transferProofCard?: ReactNode;
}

interface AmountSummaryProps {
  icon: ReactNode,
  label: string,
  amount: string,
  status: keyof typeof StatusEnums,
}

const {
  danger, gray, primary, warning,
} = BlueBirdTheme.colors;

const EmptyContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 16px;
`;

const AmountSummary = ({
  icon,
  label,
  amount,
  status,
}: AmountSummaryProps) => (
  <StatusBox status={StatusEnums[status]}>
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{
        backgroundColor: generateColor(StatusEnums[status]).text,
        padding: 8,
        borderRadius: 16,
        width: 32,
        height: 32,
        boxSizing: "border-box",
      }}>
        {icon}
      </div>
      <div>
        <Typography level={6}>{label}</Typography>
        <br />
        <Typography level={6}>{amount}</Typography>
      </div>
    </div>
  </StatusBox>
);

interface StatusConfig {
  label: string;
  backgroundColor: string;
  color: string;
  icon: React.ReactNode;
}

interface StatusWrapperProps {
  status: string | null | undefined;
}

export const StatusWrapper: React.FC<StatusWrapperProps> = ({ status }) => {
  const STATUS_MAP: Record<string, StatusConfig> = {
    "PROSES TRANSFER": {
      label: "Menunggu transfer",
      backgroundColor: "#BE321E",
      color: "#FFFFFF",
      icon: <WaitingIcon />,
    },
    CLOSE: {
      label: "Sudah transfer",
      backgroundColor: "#005EB8",
      color: "#FFFFFF",
      icon: <CheckIcon />,
    },
  };
  const DEFAULT_STATUS: StatusConfig = {
    label: "-",
    backgroundColor: "#F3F4F6",
    color: "#4B5563",
    icon: <InfoIcon />,
  };
  const config = STATUS_MAP[status || ""] || DEFAULT_STATUS;
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "8px 12px",
        borderRadius: "16px",
        backgroundColor: config.backgroundColor,
        color: config.color,
        marginLeft: "auto",
      }}
    >
      {config.icon}
      <Typography level={7}>{config.label}</Typography>
    </div>
  );
};

const WhiteReceiptContent = ({ useCustomHook, transferProofCard }: WhiteReceiptContentProps) => {
  const {
    pageName,
    contentType,
    dataWhiteReceipt,
    selectedRows,

    setExtendConfirmModal,
    setSelectedWhiteReceipt,
    setVerifyTransferModal,
    setCloseConfirm,
  } = useCustomHook;
  const { rbacInfo } = useAuth();
  const snackbar = useSnackbar();

  const isCreator = getHasAccessAction({
    menus: rbacInfo,
    menuTitle: "Flow extend bon",
  });
  const hasAccessCloseBon = getHasAccessAction({
    menus: rbacInfo,
    menuTitle: "Flow close bon",
  });
  const isTreasury = getHasAccessAction({
    menus: rbacInfo,
    menuTitle: "Verifikasi bon",
  });

  const isMaxExtend = (
    dataWhiteReceipt
    && (dataWhiteReceipt.detail_bon_merah.extended_bm
      === dataWhiteReceipt.detail_bon_merah.max_extension)
  );
  const isExpire = (
    dataWhiteReceipt
    && dataWhiteReceipt.detail_bon_merah.warning === "Y"
  );
  const isOnApprovalProgress = (
    dataWhiteReceipt
    && dataWhiteReceipt.detail_bon_merah.extended_status === "APPROVAL"
  );
  const isReceiptClosed = (
    selectedRows[0]
    && selectedRows[0].status_trx === "CLOSE"
  );
  const isOnVerificationProgress = (
    dataWhiteReceipt
    && dataWhiteReceipt.detail_history_closebm.status_trx === "VERIFIKASI"
  );

  const isDisplayExtendButton = (
    dataWhiteReceipt
    && isCreator
    && isExpire
    && !isMaxExtend
    && !isOnApprovalProgress
    && !isReceiptClosed
    && !isOnVerificationProgress
  );
  const isDisplayVerifyButton = (
    dataWhiteReceipt
    && hasAccessCloseBon
    && !isReceiptClosed
    && !isOnVerificationProgress
  );
  const isNeedExtend = (
    dataWhiteReceipt
    && isCreator
    && isExpire
    && !dataWhiteReceipt.detail_bon_merah.extended_status
    && !isReceiptClosed
    && !isOnVerificationProgress
  );
  const isApprovalProgressMessage = (
    dataWhiteReceipt
    && isCreator
    && isOnApprovalProgress
  );
  const isDisplayCloseButton = (
    dataWhiteReceipt
    && isTreasury
    && isOnVerificationProgress
    && !!dataWhiteReceipt.detail_history_closebm.file_receipt
    && pageName === "Verifikasi-bon"
  );

  const extendMessage = isMaxExtend
    ? `Perpanjangan masa aktif bon hanya dapat dilakukan ${dataWhiteReceipt.detail_bon_merah.max_extension} kali`
    : "Harap perpanjang untuk mengaktifkan Close Bon kembali";

  const bankDescription = dataWhiteReceipt
    ? `${dataWhiteReceipt.source_bank_info.source_bank_code} • ${dataWhiteReceipt.source_bank_info.source_bank_account_number} • ${dataWhiteReceipt.source_bank_info.source_bank_account_name}`
    : "";

  const copyBankAccount = () => {
    try {
      navigator.clipboard.writeText(
        dataWhiteReceipt
          ? dataWhiteReceipt.source_bank_info.source_bank_account_number
          : "",
      );
      snackbar.show("Nomor rekening berhasil disalin.", "success");
    } catch (error) {
      snackbar.show("Nomor rekening gagal disalin.", "error");
    }
  };

  return ((
    contentType !== ContentType.SELECTED
    || !dataWhiteReceipt
  )
    ? <div style={{
      display: "flex", flexDirection: "column", justifyContent: "center", height: "100%",
    }}>
      {contentType === ContentType.NOT_SELECTED && <EmptyContentWrapper>
        <img width="100%" height={120} style={{ objectFit: "contain" }} src={customerServiceImg} />
        <Typography level={5} fontWeight="medium" color={gray.subtitle}>Silakan pilih bon merah terlebih dahulu untuk menampilkan bon putih ijiran</Typography>
      </EmptyContentWrapper>}
      {contentType === ContentType.SUCCESS && <EmptyContentWrapper>
        <img width="100%" height={120} style={{ objectFit: "contain" }} src={thumbsUpImg} />
        <Typography level={5} fontWeight="medium" color={gray.subtitle}>Bon putih ijiran berhasil close</Typography>
      </EmptyContentWrapper>}
    </div>
    : <>
      {isNeedExtend
        && <div style={{
          display: "flex",
          gap: 16,
          padding: "12px 16px",
          alignItems: "center",
          borderRadius: "4px",
          border: `1px solid ${danger[600]}`,
          backgroundColor: danger[200],
          color: danger[500],
        }}>
          <WarningIcon />
          <div>
            <Typography level={5} color={danger[600]} fontWeight="bold">Masa aktif bon telah kadaluarsa</Typography>
            <br />
            <Typography level={6} color={danger[600]}>
              {extendMessage}
            </Typography>
          </div>
        </div>
      }
      {isDisplayVerifyButton
        && <div style={{
          display: "flex",
          gap: 16,
          flexDirection: "column",
          padding: "12px 16px",
          alignItems: "stretch",
          borderRadius: "4px",
          border: `1px solid ${warning[600]}`,
          backgroundColor: warning[200],
          color: warning[600],
        }}>
          <div style={{
            display: "flex",
            gap: 16,
            alignItems: "center",
          }}>
            <InfoIcon />
            <div>
              <Typography level={5} color={warning[600]} fontWeight="bold">Selisih ditransfer secara manual</Typography>
              <br />
              <Typography level={6} color={warning[600]}>
                Transfer ke rekening dibawah lalu upload bukti transfer untuk Close bon
              </Typography>
            </div>
          </div>
          <Divider />
          <div
            style={{
              display: "flex",
              gap: 16,
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={copyBankAccount}
          >
            <BankIcon />
            <div style={{ width: "-webkit-fill-available" }}>
              <Typography level={5} color={warning[600]} fontWeight="bold">{bankDescription}</Typography>
            </div>
            <Tooltip
              direction="bottom"
              tooltipContent="Salin nomor rekening"
            >
              <CopyIcon />
            </Tooltip>
          </div>
        </div>
      }
      {isApprovalProgressMessage
        && <div style={{
          display: "flex",
          gap: 16,
          padding: "12px 16px",
          alignItems: "center",
          borderRadius: "4px",
          border: `1px solid ${primary[600]}`,
          backgroundColor: primary[200],
          color: primary[500],
        }}>
          <InfoIcon />
          <div>
            <Typography level={5} color={primary[600]} fontWeight="bold">Perpanjang masa aktif bon sedang diproses</Typography>
            <br />
            <Typography level={6} color={primary[600]}>
              Harap ditunggu sampai proses selesai
            </Typography>
          </div>
        </div>
      }
      {(
        dataWhiteReceipt.detail_history_closebm.file_receipt
        && isOnVerificationProgress
        && pageName === "Close-bon-putih-ijiran"
      )
        && <div style={{
          display: "flex",
          gap: 16,
          padding: "12px 16px",
          alignItems: "center",
          borderRadius: "4px",
          border: `1px solid ${primary[600]}`,
          backgroundColor: primary[200],
          color: primary[500],
        }}>
          <InfoIcon />
          <div>
            <Typography level={5} color={primary[600]} fontWeight="bold">Menunggu verifikasi</Typography>
            <br />
            <Typography level={6} color={primary[600]}>
              Bukti transfer telah dikirim, menunggu verifikasi dari treasury
            </Typography>
          </div>
        </div>
      }
      {(dataWhiteReceipt.detail_history_closebm.file_receipt) && transferProofCard}
      <ThemeBackground style={{ display: "flex", gap: 24, alignItems: "center" }}>
        <img src={avatarImg} width={80} height={80} />
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Typography level={4} fontWeight="medium" color={gray.subtitle}>{dataWhiteReceipt.detail_bon_merah.employee_name}</Typography>
          <Typography level={6} fontWeight="medium" color={gray.subtitle}>{dataWhiteReceipt.detail_bon_merah.employee_id}</Typography>
        </div>
      </ThemeBackground>
      <Divider color={primary[200]} />
      <div style={{ overflowY: "auto", minHeight: 100, height: "100%" }}>
        {dataWhiteReceipt.list_bon_putih_ijir.map((item) => {
          const isBmc = item.status_trx_bmc != null && item.trx_id.startsWith("BMC");
          return (
            <div style={{
              display: "flex",
              flexDirection: "column",
              gap: 20,
              height: "fit-content",
            }}>
              <div style={{
                border: isBmc ? "1px solid #BE321E" : "none",
                padding: isBmc ? "1rem" : "initial",
                borderRadius: isBmc ? "16px" : "initial",
              }}>
                {isBmc && (
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}>
                    <Typography level={5} fontWeight="bold">{item.trx_id}</Typography>
                    <StatusWrapper status={item.status_trx_bmc} />
                  </div>
                )}
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}>
                  <div style={{ width: "calc(100% - 48px - 16px)" }}>
                    <Typography level={7} color={gray.subtitle}>
                      {toStringDate(item.trx_date)}
                    </Typography>
                    {!isBmc && (
                      <Typography level={7} color={gray.subtitle} fontWeight="medium"> • No. bon putih {item.trx_id}</Typography>
                    )}
                    <br />
                    <Typography level={5} color={primary[500]} fontWeight="bold">Bon : {toRupiah(item.amount)}</Typography>
                    <br />
                    <Typography level={7} color={gray.subtitle} fontWeight="regular">Akun GL : </Typography>
                    <Typography level={7} color={gray.subtitle} fontWeight="bold">
                      <span style={{ display: "inline-flex" }}>
                        {item.trx_code_detail.trx_code}
                        &nbsp;•&nbsp;
                        {CommonTrxTypes.find(
                          (type) => type.value === item.trx_code_detail.sap_shkzg,
                        )?.label}
                        <br />
                        {item.trx_code_detail.trx_desc}
                      </span>
                    </Typography>
                    <br />
                    <div className="ellipsis-text">
                      <Typography level={7} color={gray.bodyText} fontWeight="regular">{item.trx_text}</Typography>
                    </div>
                  </div>
                  <div
                    style={{ margin: isBmc ? "auto 0" : "" }}
                  >
                    <Button variant="tertiary" size="sm" onClick={() => setSelectedWhiteReceipt(item)} ><CornerArrowIcon /></Button>
                  </div>
                </div>
              </div>
              <Divider color={primary[200]} />
            </div>
          );
        })}
        {dataWhiteReceipt.list_bon_putih_ijir.length === 0
          && <div style={{
            display: "flex", flexDirection: "column", justifyContent: "center", height: "100%",
          }}>
            <EmptyContentWrapper>
              <img width="100%" height={80} style={{ objectFit: "contain" }} src={customerServiceImg} />
              <Typography level={5} fontWeight="medium" color={gray.subtitle}>
                Bon merah belum diijir</Typography>
            </EmptyContentWrapper>
          </div>
        }
      </div>
      <div style={{ display: "flex", gap: 16 }}>
        <AmountSummary
          status={StatusEnums.DANGER}
          icon={<MoneyIcon width={16} height={16} />}
          label="Bon merah"
          amount={toRupiah(dataWhiteReceipt.compare_amount_bon.amount_bon_merah)}
        />
        <AmountSummary
          status={StatusEnums.INACTIVE}
          icon={<MoneyIcon width={16} height={16} />}
          label="Bon putih ijiran"
          amount={toRupiah(dataWhiteReceipt.compare_amount_bon.amount_bon_putih_ijir)}
        />
      </div>
      <AmountSummary
        status={StatusEnums.PRIMARY}
        icon={<MoneyIcon width={16} height={16} />}
        label="Selisih"
        amount={toRupiah(dataWhiteReceipt.compare_amount_bon.amount_margin, true)}
      />
      {isDisplayExtendButton
        && <Button
          variant="secondary"
          onClick={() => setExtendConfirmModal(true)}
          disabled={isOnApprovalProgress}
        >Perpanjang masa aktif</Button>
      }
      {isDisplayVerifyButton
        && <Button
          onClick={() => setVerifyTransferModal(true)}
        >Ajukan verifikasi transfer</Button>
      }
      {isDisplayCloseButton
        && <Button
          onClick={() => setCloseConfirm(true)}
        >Verifikasi & Close bon</Button>
      }
    </>
  );
};

export default WhiteReceiptContent;
